import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React from "react";
import { CSSTransition } from "react-transition-group";

import ErrorBoundary from "../../containers/App/addons/error-boundary";
import type { ButtonStyleProps } from "../button/styles";
import LoadingSpinner from "../loading-spinner";
import { Loading, StyledUpButton, Value } from "./styles";

type NativeButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "onClick" | "type" | "value"
>;

interface UpButtonBaseProps extends NativeButtonProps, ButtonStyleProps {
  disabledButtonColor?: string;
  gTagId?: string;
  loading?: boolean;
  successfull?: boolean;
  successfullText?: React.ReactNode;
  value: React.ReactNode;
}

type UpButtonInteractionProps =
  | { type: "submit"; onClick?: () => void }
  | { type: "button" | "reset"; onClick: () => void };

export type UpButtonProps = UpButtonBaseProps & UpButtonInteractionProps;

const UpButton = ({
  value,
  loading = false,
  margin,
  type,
  disabled = false,
  onClick,
  padding,
  successfull,
  successfullText = "Saved",
  buttonColor,
  disabledButtonColor,
  gTagId,
  ...props
}: UpButtonProps) => {
  const successNodeRef = React.useRef<HTMLDivElement>(null);

  return (
    <ErrorBoundary>
      <StyledUpButton
        id={gTagId}
        buttonColor={buttonColor}
        disabledButtonColor={disabledButtonColor}
        onClick={() => {
          if (type !== "submit") {
            onClick();
          }
        }}
        margin={margin}
        type={type}
        disabled={disabled}
        position="relative"
        padding={padding}
        {...props}
      >
        {loading ? (
          <Loading>
            <LoadingSpinner size="17px" />
          </Loading>
        ) : null}

        <CSSTransition
          nodeRef={successNodeRef}
          in={successfull}
          classNames="successfull"
          timeout={200}
          unmountOnExit
        >
          <Loading ref={successNodeRef}>
            {successfullText}
            <FontAwesomeIcon
              icon="check"
              style={{ marginLeft: "6px", fontSize: "12px" }}
            />
          </Loading>
        </CSSTransition>
        <Value className={loading || successfull ? "loading" : ""}>{value}</Value>
      </StyledUpButton>
    </ErrorBoundary>
  );
};

UpButton.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  loading: PropTypes.bool,
  margin: PropTypes.string,
  type: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  padding: PropTypes.string,
  successfull: PropTypes.bool,
  successfullText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  buttonColor: PropTypes.string,
  disabledButtonColor: PropTypes.string
};

export default UpButton;
